import React, { useCallback } from 'react'
import { DndProvider } from 'react-dnd'
import Backend from 'react-dnd-html5-backend'
import { Field, Form } from 'react-final-form'
import { Button } from '@material-ui/core'
import { injectIntl } from 'react-intl'
import update from 'immutability-helper'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Input from 'components/common/Form/Input'
import getValidator, { VALIDATOR_IS_EMPTY } from 'validation'
import ModalWindow from 'components/common/Modals/ModalWindow'
import {
  createLookupTable,
  fetchLookupTablesData,
} from 'reducers/lookupTables/reducer'
import withPromise from 'helpers/withPromise'
import { REACT_INTL_PROP_TYPE } from 'constants/propTypes'
import messages from './translate'
import DraggableColumnCard from './DraggableColumnCard'

function CreateTableModalForm({
  intl: { formatMessage },
  isCreateTableModalOpened,
  onCloseCreateTableModal,
  tableTitle,
  setTableTitle,
  tableColumns,
  setTableColumns,
  createLookupTable,
  fetchLookupTablesData,
  tablesListPsfs,
}) {
  const onChangeColumnTitle = (value, id) => {
    const updatedColumns = tableColumns.map(column => {
      if (id === column.id) {
        return {
          id,
          title: value,
        }
      }

      return column
    })

    setTableColumns(updatedColumns)

    return value
  }

  const onRemoveColumn = index => {
    const updatedColumns = tableColumns.filter(
      (column, updatedColumnIndex) => index !== updatedColumnIndex,
    )

    setTableColumns(updatedColumns)
  }

  const onCreateTableSubmit = ({ title, columns }) => {
    const data = {
      title,
      columns: columns.map(({ title: columnTitle }) => ({
        title: columnTitle,
      })),
    }

    withPromise(createLookupTable, {
      data,
      successMessage: formatMessage(messages.successLookupTableCreation),
      errorMessage: formatMessage(messages.failureLookupTableCreation),
    }).then(response => {
      if (!response.error) {
        onCloseCreateTableModal(true)
        fetchLookupTablesData({
          psfs: tablesListPsfs,
        })
      }
    })
  }

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const dragColumns = tableColumns[dragIndex]

      setTableColumns(
        update(tableColumns, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragColumns],
          ],
        }),
      )
    },
    [tableColumns],
  )

  return (
    <ModalWindow
      open={isCreateTableModalOpened}
      onClose={onCloseCreateTableModal}>
      <div className="modal-box-title-holder">
        <div className="modal-box-title">
          {formatMessage(messages.newTable)}
        </div>
        <div className="modal-box-x-button" onClick={onCloseCreateTableModal} />
      </div>
      <Form
        onSubmit={onCreateTableSubmit}
        initialValues={{
          title: tableTitle,
          columns: tableColumns,
        }}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="title"
              type="text"
              label={formatMessage(messages.tableName)}
              component={Input}
              disabled={submitting}
              required
              validate={getValidator(VALIDATOR_IS_EMPTY)}
              parse={value => {
                setTableTitle(value)

                return value
              }}
            />
            {!!tableColumns.length && (
              <>
                <div className="modal-box-subtitle">
                  {formatMessage(messages.columns)}
                </div>
                <DndProvider backend={Backend}>
                  <div className="table-column-list">
                    {tableColumns.map(({ id }, index) => (
                      <DraggableColumnCard
                        key={id}
                        index={index}
                        id={id}
                        submitting={submitting}
                        onChangeColumnTitle={onChangeColumnTitle}
                        onRemoveColumn={onRemoveColumn}
                        moveCard={moveCard}
                      />
                    ))}
                  </div>
                </DndProvider>
              </>
            )}
            <Button
              className="add-column-button"
              onClick={() => {
                setTableColumns([
                  ...tableColumns,
                  {
                    id: Math.random(),
                    title: '',
                  },
                ])
              }}>
              <span className="add-column-button-label">
                {formatMessage(messages.addColumn)}
              </span>
            </Button>
            <Button
              className="lookup-tables-button primary"
              onClick={handleSubmit}>
              {formatMessage(messages.createButton)}
            </Button>
          </form>
        )}
      />
    </ModalWindow>
  )
}

CreateTableModalForm.propTypes = {
  intl: REACT_INTL_PROP_TYPE.isRequired,
  isCreateTableModalOpened: PropTypes.bool.isRequired,
  onCloseCreateTableModal: PropTypes.func.isRequired,
  tableTitle: PropTypes.string.isRequired,
  setTableTitle: PropTypes.func.isRequired,
  tableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
  ).isRequired,
  setTableColumns: PropTypes.func.isRequired,
  createLookupTable: PropTypes.func.isRequired,
  fetchLookupTablesData: PropTypes.func.isRequired,
}

export default connect(
  state => ({
    tablesListPsfs: state.getIn(['lookupTables', 'tablesListPsfs']),
  }),
  {
    createLookupTable,
    fetchLookupTablesData,
  },
)(injectIntl(CreateTableModalForm))
